import styled from 'styled-components';
import {media} from '../../styles/components/media-queries';

export const PageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 100rem;
  margin: 4rem auto 2rem;
  padding: 0 2rem;
  ${media.smaller`
    margin: 1rem auto;
    padding: 0 1rem;
  `};
`;

export const PageTitle = styled.h1`
  color: ${p => p.theme.secondaryColor};
  font-size: 3.6rem;
  font-weight: lighter;
  margin-bottom: 2rem;
  ${media.smaller`
    font-size: 2.4rem;
    margin-bottom: 1rem;
  `};
`;

export const SectionTitle = styled.h2`
  color: ${p => p.theme.secondaryColor};
  font-size: 2.4rem;
  font-weight: lighter;
  margin: 2rem 0 1rem;
  ${media.smaller`
    font-size: 1.8rem;
  `};
`;

export const PageIntro = styled.div`
  font-size: 1.6rem;
  line-height: 2.4rem;
  color: ${p => p.theme.textColor};
  margin-bottom: 2rem;

  p {
    margin: 0 0 1rem;
  }

  strong {
    color: ${p => p.theme.secondaryColor};
  }
`;

export const UploadSection = styled.div`
  margin-bottom: 3rem;
  padding: 2rem;
  border: 0.1rem solid ${p => p.theme.borderColor};
  border-radius: 0.4rem;
  background-color: ${p => p.theme.backgroundColor || '#fff'};
  position: relative;
`;

export const DropZone = styled.div`
  border: 0.2rem dashed
    ${p => (p.$isDragOver ? p.theme.secondaryColor : p.theme.borderColor)};
  border-radius: 0.4rem;
  padding: 3rem;
  text-align: center;
  cursor: pointer;
  transition: border-color 0.2s ease, background-color 0.2s ease;
  background-color: ${p =>
    p.$isDragOver ? 'rgba(0, 128, 0, 0.05)' : 'transparent'};

  &:hover {
    border-color: ${p => p.theme.secondaryColor};
  }

  p {
    font-size: 1.6rem;
    color: ${p => p.theme.textColor};
    margin: 0.5rem 0;
  }
`;

export const DropZoneLabel = styled.p`
  font-size: 1.8rem !important;
  font-weight: bold;
  color: ${p => p.theme.secondaryColor} !important;
`;

export const FileList = styled.div`
  margin-top: 1.5rem;
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
`;

export const FileChip = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background-color: ${p => p.theme.lightBackgroundColor || '#f5f5f5'};
  border-radius: 0.4rem;
  font-size: 1.4rem;
  color: ${p => p.theme.textColor};

  button {
    background: none;
    border: none;
    cursor: pointer;
    font-size: 1.6rem;
    color: ${p => p.theme.tertiaryColor};
    padding: 0;
    line-height: 1;
  }
`;

export const UploadButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 1.5rem;
  padding: 1.2rem 3rem;
  font-size: 1.6rem;
  font-weight: bold;
  text-transform: uppercase;
  color: ${p => p.theme.primaryTextColor};
  background-color: ${p => p.theme.secondaryColor};
  border: none;
  border-radius: 0.4rem;
  cursor: pointer;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.9;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const UploadingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(255, 255, 255, 0.9);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1.5rem;
  z-index: 1;
  border-radius: 0.4rem;

  p {
    font-size: 1.6rem;
    color: ${p => p.theme.secondaryColor};
    font-weight: bold;
  }

  span {
    font-size: 1.4rem;
    color: ${p => p.theme.tertiaryTextColor};
  }
`;

export const SuccessMessage = styled.div`
  margin-top: 1.5rem;
  padding: 1.5rem;
  background-color: rgba(0, 128, 0, 0.08);
  border: 0.1rem solid #2e7d32;
  border-radius: 0.4rem;
  font-size: 1.4rem;
  color: #2e7d32;
`;

export const ErrorMessage = styled.div`
  margin-top: 1.5rem;
  padding: 1.5rem;
  background-color: rgba(211, 47, 47, 0.08);
  border: 0.1rem solid #d32f2f;
  border-radius: 0.4rem;
  font-size: 1.4rem;
  color: #d32f2f;
`;

export const InvoiceTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 1.4rem;

  th {
    text-align: left;
    padding: 1.2rem 1rem;
    border-bottom: 0.2rem solid ${p => p.theme.borderColor};
    color: ${p => p.theme.secondaryColor};
    font-weight: bold;
    white-space: nowrap;
  }

  td {
    padding: 1.2rem 1rem;
    border-bottom: 0.1rem solid ${p => p.theme.borderColor};
    color: ${p => p.theme.textColor};
  }

  tbody tr {
    cursor: pointer;
    transition: background-color 0.15s ease;

    &:hover {
      background-color: ${p => p.theme.lightBackgroundColor || '#f9f9f9'};
    }
  }

  ${media.small`
    th:nth-child(4),
    td:nth-child(4),
    th:nth-child(5),
    td:nth-child(5) {
      display: none;
    }
  `};

  ${media.smaller`
    th:nth-child(3),
    td:nth-child(3) {
      display: none;
    }
  `};
`;

export const StatusBadge = styled.span`
  && {
    display: inline-block;
    padding: 0.3rem 0.8rem;
    border-radius: 0.3rem;
    font-size: 1.2rem;
    font-weight: bold;
    white-space: nowrap;
    color: ${p => {
      switch (p.$status) {
        case 'READY_FOR_REVIEW':
        case 'SUBMITTED':
          return '#6d5600';
        default:
          return '#fff';
      }
    }};
    background-color: ${p => {
      switch (p.$status) {
        case 'PROCESSING':
          return '#1976d2';
        case 'READY_FOR_REVIEW':
        case 'SUBMITTED':
          return '#fff3cd';
        case 'MATCHED':
        case 'EMAIL_SENT':
          return '#2e7d32';
        case 'FAILED':
          return '#d32f2f';
        default:
          return '#757575';
      }
    }};
  }
`;

export const Pagination = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 1rem;
  margin: 2rem 0;
  font-size: 1.4rem;

  button {
    padding: 0.8rem 1.6rem;
    font-size: 1.4rem;
    cursor: pointer;
    background-color: ${p => p.theme.secondaryColor};
    color: ${p => p.theme.primaryTextColor};
    border: none;
    border-radius: 0.4rem;

    &:disabled {
      opacity: 0.4;
      cursor: not-allowed;
    }
  }

  span {
    color: ${p => p.theme.textColor};
  }
`;

export const ViewLink = styled.span`
  font-size: 1.3rem;
  font-weight: 600;
  color: ${p => p.theme.secondaryColor};
  white-space: nowrap;
`;

export const EmptyState = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  font-size: 1.6rem;
  color: ${p => p.theme.tertiaryTextColor};
`;

export const LoadingWrapper = styled.div`
  display: flex;
  justify-content: center;
  padding: 4rem 0;
`;

export const DetailWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 100rem;
  margin: 4rem auto 2rem;
  padding: 0 2rem;
  ${media.smaller`
    margin: 1rem auto;
    padding: 0 1rem;
  `};
`;

export const BackLink = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  font-size: 1.4rem;
  color: ${p => p.theme.secondaryColor};
  padding: 0;
  margin-bottom: 1rem;
  text-decoration: underline;

  &:hover {
    opacity: 0.8;
  }
`;

export const OverviewCard = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 2rem;
  padding: 2rem;
  border: 0.1rem solid ${p => p.theme.borderColor};
  border-radius: 0.4rem;
  margin-bottom: 2rem;
  background-color: ${p => p.theme.backgroundColor || '#fff'};
`;

export const OverviewItem = styled.div`
  min-width: 15rem;

  label {
    display: block;
    font-size: 1.2rem;
    color: ${p => p.theme.tertiaryTextColor};
    text-transform: uppercase;
    margin-bottom: 0.3rem;
  }

  > span {
    font-size: 1.6rem;
    color: ${p => p.theme.textColor};
    font-weight: ${p => (p.$bold ? 'bold' : 'normal')};
  }
`;

export const SavingsSummary = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 2rem;
  padding: 2rem;
  border: 0.1rem solid #2e7d32;
  border-radius: 0.4rem;
  margin-bottom: 2rem;
  background-color: rgba(0, 128, 0, 0.04);
`;

export const SavingsValue = styled.div`
  min-width: 15rem;

  label {
    display: block;
    font-size: 1.2rem;
    color: ${p => p.theme.tertiaryTextColor};
    text-transform: uppercase;
    margin-bottom: 0.3rem;
  }

  span {
    font-size: 2rem;
    font-weight: bold;
    color: ${p => (p.$positive ? '#2e7d32' : p.theme.textColor)};
  }
`;

export const LineItemCard = styled.div`
  border: 0.1rem solid ${p => p.theme.borderColor};
  border-radius: 0.4rem;
  padding: 1.5rem;
  margin-bottom: 1rem;
  background-color: ${p => p.theme.backgroundColor || '#fff'};
`;

export const LineItemHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1rem;
  gap: 1rem;
  ${media.smaller`
    flex-direction: column;
  `};
`;

export const LineItemDescription = styled.div`
  font-size: 1.6rem;
  font-weight: bold;
  color: ${p => p.theme.textColor};
`;

export const LineItemDetails = styled.div`
  font-size: 1.4rem;
  color: ${p => p.theme.tertiaryTextColor};
  margin-top: 0.3rem;
`;

export const LineItemMatch = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  border-radius: 0.4rem;
  background-color: ${p => p.theme.lightBackgroundColor || '#f9f9f9'};
  gap: 1rem;
  ${media.smaller`
    flex-direction: column;
    align-items: flex-start;
  `};
`;

export const MatchInfo = styled.div`
  font-size: 1.4rem;
  color: ${p => p.theme.textColor};

  strong {
    display: block;
    margin-bottom: 0.2rem;
  }
`;

export const ProductLink = styled.a`
  color: ${p => p.theme.secondaryColor};
  text-decoration: none;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;

export const SavingBadge = styled.span`
  font-size: 1.4rem;
  font-weight: bold;
  padding: 0.4rem 0.8rem;
  border-radius: 0.3rem;
  white-space: nowrap;
  color: #fff;
  background-color: ${p => (p.$positive ? '#2e7d32' : '#d32f2f')};
`;

export const NoMatchLabel = styled.span`
  font-size: 1.4rem;
  color: ${p => p.theme.tertiaryTextColor};
  font-style: italic;
`;

export const ProcessingMessage = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  font-size: 1.6rem;
  color: ${p => p.theme.tertiaryTextColor};

  h3 {
    color: ${p => p.theme.secondaryColor};
    margin-bottom: 1rem;
  }
`;
